import React, { useState, useMemo } from "react";
import { History, FileSpreadsheet, Loader2, User, Clock, ChevronRight, X, Lock, Trash2, MapPin, CalendarDays, Layers, Download, Columns } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useGlobal } from "@/context/GlobalContext";
import { ExcelGrid, SheetTabs, exportSheetsToExcel } from "@/components/ExcelGrid";
import { SEMANAL } from "@/lib/resumenConfig";

const periodOf = (it) => (it?.period_month ? `${MESES[it.period_month - 1]} ${it.period_year}` : `Año ${it?.period_year ?? ""}`);

const MESES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const YEARS = Array.from({ length: 7 }, (_, i) => 2024 + i);

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-MX", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

function DetailModal({ id, onClose, base }) {
  const [active, setActive] = useState(0);
  const { data, isLoading } = useQuery({
    queryKey: ["resumen-detalle", base, id],
    queryFn: async () => (await api.get(`${base}/${id}`)).data,
    enabled: !!id,
  });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" data-testid="resumen-detail-modal" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between p-5 border-b border-slate-100">
          <div>
            <div className="flex items-center gap-2">
              <p className="text-[11px] font-bold uppercase tracking-widest text-red-600">Importación · Solo lectura</p>
              <span className="inline-flex items-center gap-1 text-[10px] font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full"><Lock className="w-3 h-3" />No editable</span>
            </div>
            <h3 className="text-2xl font-extrabold text-slate-900 mt-0.5" data-testid="resumen-detail-title">{data?.sucursal_name ? `${data.sucursal_name} · ` : ""}{data?.period_label || "…"}</h3>
            {data && (
              <p className="text-xs text-slate-500 mt-1">{data.filename} · {data.record_count} registros · {data.imported_by_name || data.imported_by_email} · {fmtDate(data.imported_at)}</p>
            )}
          </div>
          <button onClick={onClose} data-testid="resumen-detail-close" className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-auto p-5 space-y-3 min-h-[300px]">
          {isLoading ? (
            <div className="h-64 flex items-center justify-center"><Loader2 className="w-8 h-8 text-red-600 animate-spin" /></div>
          ) : (
            <>
              <SheetTabs sheets={data?.sheets} active={active} onChange={setActive} />
              <ExcelGrid sheet={data?.sheets?.[active]} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function CompareModal({ versions, onClose, base }) {
  const [aId, setAId] = useState(versions[0]?.id);
  const [bId, setBId] = useState(versions[1]?.id || versions[0]?.id);
  const [aAct, setAAct] = useState(0);
  const [bAct, setBAct] = useState(0);
  const qa = useQuery({ queryKey: ["cmp", base, aId], queryFn: async () => (await api.get(`${base}/${aId}`)).data, enabled: !!aId });
  const qb = useQuery({ queryKey: ["cmp", base, bId], queryFn: async () => (await api.get(`${base}/${bId}`)).data, enabled: !!bId });
  const label = (v, i) => `${i === 0 ? "Más reciente · " : ""}${fmtDate(v.imported_at)}`;
  const Col = ({ id, setId, doc, act, setAct, tid }) => (
    <div className="flex-1 min-w-0 flex flex-col border border-slate-200 rounded-xl overflow-hidden">
      <div className="p-2 border-b border-slate-100 bg-slate-50 flex items-center gap-2">
        <Select value={id} onValueChange={setId}>
          <SelectTrigger className="h-8 flex-1 bg-white border-slate-200 text-xs" data-testid={`cmp-select-${tid}`}><SelectValue /></SelectTrigger>
          <SelectContent>{versions.map((v, i) => <SelectItem key={v.id} value={v.id}>{label(v, i)}</SelectItem>)}</SelectContent>
        </Select>
        {doc && <button onClick={() => exportSheetsToExcel(doc.sheets, `${doc.sucursal_name}_${doc.period_label}_${tid}`)} className="h-8 px-2 rounded-lg bg-emerald-600 text-white text-xs flex items-center gap-1"><Download className="w-3 h-3" /></button>}
      </div>
      <div className="flex-1 overflow-auto p-2">
        {!doc ? <div className="h-40 flex items-center justify-center"><Loader2 className="w-6 h-6 text-red-600 animate-spin" /></div> : (
          <><SheetTabs sheets={doc.sheets} active={act} onChange={setAct} /><div className="mt-2"><ExcelGrid sheet={doc.sheets?.[act]} /></div></>
        )}
      </div>
    </div>
  );
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" data-testid="resumen-compare-modal" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[95vw] max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between p-5 border-b border-slate-100">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-widest text-red-600">Comparar versiones</p>
            <h3 className="text-xl font-extrabold text-slate-900 mt-0.5">{versions[0]?.sucursal_name} · {periodOf(versions[0])}</h3>
          </div>
          <button onClick={onClose} data-testid="resumen-compare-close" className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-hidden p-5 flex gap-4">
          <Col id={aId} setId={setAId} doc={qa.data} act={aAct} setAct={setAAct} tid="a" />
          <Col id={bId} setId={setBId} doc={qb.data} act={bAct} setAct={setBAct} tid="b" />
        </div>
      </div>
    </div>
  );
}

export default function ResumenHistorial({ cfg = SEMANAL }) {
  const { user } = useAuth();
  const { currentStructure, structureKey, node } = useGlobal();
  const contexto = node?.path?.length ? node.path.map((p) => p.name).join(" › ") : "Toda la estructura";
  const qc = useQueryClient();
  const [selected, setSelected] = useState(null);
  const [compare, setCompare] = useState(null);
  const exportVersion = async (id) => {
    try {
      const { data } = await api.get(`${cfg.base}/${id}`);
      exportSheetsToExcel(data.sheets, `${data.sucursal_name || "resultado"}_${data.period_label || ""}`);
    } catch { toast.error("No se pudo exportar."); }
  };
  const [fMonth, setFMonth] = useState("all");
  const [fYear, setFYear] = useState("all");

  const nodeId = node?.id ?? "entire";
  const { data, isLoading } = useQuery({
    queryKey: [`${cfg.qk}-historial`, structureKey, nodeId],
    queryFn: async () => (await api.get(`${cfg.base}/historial`, { params: { structure: structureKey, node_id: node?.id } })).data,
    enabled: !!structureKey,
  });
  const allItems = data?.items || [];

  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`${cfg.base}/${id}`)).data,
    onSuccess: () => { toast.success("Importación eliminada."); qc.invalidateQueries({ queryKey: [`${cfg.qk}-historial`] }); qc.invalidateQueries({ queryKey: [`${cfg.qk}-ultimo`] }); },
    onError: (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "No se pudo eliminar."),
  });

  // Agrupa por periodo+sucursal para mostrar versiones juntas.
  const groups = useMemo(() => {
    const items = allItems.filter((it) =>
      (fMonth === "all" || String(it.period_month) === fMonth) &&
      (fYear === "all" || String(it.period_year) === fYear)
    );
    const map = new Map();
    for (const it of items) {
      const key = `${it.period_year}-${String(it.period_month).padStart(2, "0")}|${it.sucursal_name || it.sucursal_id}`;
      if (!map.has(key)) map.set(key, {
        key, label: periodOf(it),
        sucursal: it.sucursal_name || (it.sucursal_id != null ? `Sucursal ${it.sucursal_id}` : "—"),
        sortKey: `${it.period_year}-${String(it.period_month).padStart(2, "0")}`, versions: [],
      });
      map.get(key).versions.push(it);
    }
    const arr = Array.from(map.values());
    arr.forEach((g) => g.versions.sort((a, b) => (a.imported_at < b.imported_at ? 1 : -1)));
    return arr.sort((a, b) => (a.sortKey < b.sortKey ? 1 : -1) || a.sucursal.localeCompare(b.sucursal));
  }, [allItems, fMonth, fYear]);

  return (
    <div className="w-full space-y-6" data-testid={cfg.annual ? "anual-historial" : "resumen-historial"}>
      <div>
        <p className="text-[11px] font-bold uppercase tracking-widest text-red-600">Resumen Semanal</p>
        <h1 className="font-display text-4xl font-extrabold text-slate-900 mt-1">{cfg.histTitle}</h1>
        <p className="text-sm text-slate-500 mt-1">Consulta las importaciones de la estructura seleccionada por periodo y sucursal, con todas sus versiones.</p>
      </div>

      {/* Contexto de estructura */}
      <div className="flex items-center gap-2 text-sm bg-white rounded-2xl border border-slate-200/70 shadow-sm px-4 py-3" data-testid="hist-context">
        <MapPin className="w-4 h-4 text-red-600 shrink-0" />
        <span className="text-slate-400 font-semibold uppercase text-[10px] tracking-widest">Contexto</span>
        <span className="font-semibold text-slate-800 truncate">{currentStructure?.name || "—"}</span>
        <span className="text-slate-300">·</span>
        <span className="text-slate-600 truncate">{contexto}</span>
      </div>

      {/* Filtros de búsqueda */}
      <div className="bg-white rounded-2xl border border-slate-200/70 shadow-sm p-4 flex flex-wrap items-center gap-3" data-testid="resumen-hist-filters">
        <div className="flex items-center gap-2">
          <CalendarDays className="w-4 h-4 text-slate-400" />
          <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Periodo</span>
          {!cfg.annual && (
            <Select value={fMonth} onValueChange={setFMonth}>
              <SelectTrigger className="h-9 w-36 bg-slate-50 border-slate-200 text-sm" data-testid="hist-filter-mes"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="all">Todos los meses</SelectItem>
                {MESES.map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Select value={fYear} onValueChange={setFYear}>
            <SelectTrigger className="h-9 w-28 bg-slate-50 border-slate-200 text-sm" data-testid="hist-filter-anio"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="all">Todos</SelectItem>
              {YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {isLoading ? (
        <div className="h-64 flex items-center justify-center"><Loader2 className="w-8 h-8 text-red-600 animate-spin" /></div>
      ) : groups.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200/70 shadow-sm p-12 text-center" data-testid="resumen-historial-empty">
          <div className="inline-flex p-4 rounded-2xl bg-slate-50 text-slate-400"><History className="w-8 h-8" /></div>
          <p className="mt-4 text-slate-600 font-semibold">No hay importaciones para los filtros seleccionados.</p>
        </div>
      ) : (
        <div className="space-y-5">
          {groups.map((g) => (
            <div key={g.key} className="bg-white rounded-2xl border border-slate-200/70 shadow-sm p-4" data-testid={`resumen-group-${g.key}`}>
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <span className="p-1.5 rounded-lg bg-red-50 text-red-600"><MapPin className="w-4 h-4" /></span>
                <h2 className="text-base font-bold text-slate-900">{g.sucursal}</h2>
                <span className="text-slate-300">·</span>
                <span className="text-sm font-semibold text-slate-600">{g.label}</span>
                <span className="ml-2 inline-flex items-center gap-1 text-[11px] font-bold text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full"><Layers className="w-3 h-3" />{g.versions.length} {g.versions.length === 1 ? "versión" : "versiones"}</span>
                {g.versions.length >= 2 && (
                  <button data-testid={`resumen-compare-${g.key}`} onClick={() => setCompare(g.versions)} className="ml-auto h-8 px-3 rounded-lg border border-slate-200 text-xs font-semibold text-slate-600 flex items-center gap-1.5 hover:bg-slate-50 transition-colors"><Columns className="w-3.5 h-3.5" />Comparar versiones</button>
                )}
              </div>
              <div className="space-y-2">
                {g.versions.map((it, vi) => (
                  <div key={it.id} data-testid={`resumen-version-${it.id}`} onClick={() => setSelected(it.id)}
                    className="group flex items-center gap-3 rounded-xl border border-slate-100 hover:border-red-200 hover:bg-red-50/30 px-3 py-2.5 cursor-pointer transition-colors">
                    <div className="shrink-0 p-2 rounded-lg bg-emerald-50 text-emerald-600"><FileSpreadsheet className="w-4 h-4" /></div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-slate-800 text-sm truncate" title={it.filename}>{it.filename}</p>
                        {vi === 0 && <span className="text-[10px] font-bold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded">Más reciente</span>}
                        {vi > 0 && <span className="text-[10px] font-semibold text-slate-400">v{g.versions.length - vi}</span>}
                      </div>
                      <p className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-3 flex-wrap">
                        <span>{it.record_count} registros</span>
                        <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" />{fmtDate(it.imported_at)}</span>
                        <span className="inline-flex items-center gap-1"><User className="w-3 h-3" />{it.imported_by_name || it.imported_by_email || "—"}</span>
                      </p>
                    </div>
                    {user?.is_admin && (
                      <button data-testid={`resumen-delete-${it.id}`}
                        onClick={(e) => { e.stopPropagation(); if (window.confirm("¿Eliminar esta versión del historial?")) del.mutate(it.id); }}
                        className="text-slate-300 hover:text-red-600 transition-colors p-1"><Trash2 className="w-4 h-4" /></button>
                    )}
                    <button data-testid={`resumen-export-${it.id}`} onClick={(e) => { e.stopPropagation(); exportVersion(it.id); }}
                      className="text-slate-400 hover:text-emerald-600 transition-colors p-1" title="Exportar a Excel"><Download className="w-4 h-4" /></button>
                    <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-red-500 transition-colors" />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {selected && <DetailModal id={selected} base={cfg.base} onClose={() => setSelected(null)} />}
      {compare && <CompareModal versions={compare} base={cfg.base} onClose={() => setCompare(null)} />}
    </div>
  );
}
